import {BidBaseModel, BidStatus} from "./bidding_model";
import {AutosizeTextarea} from "../ui/autoresizeTextArea";
import {Button} from "../ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,} from "../ui/form";
import {Input} from "../ui/input";
import {SubmitBidDialog} from "./SubmitBid";
import {WithLoggedInAuthInfoProps, withRequiredAuthInfo,} from "@propelauth/react";
import React, {useContext, useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {useToast} from "../ui/use-toast";
import {CompanyType} from "../../models/company";
import {USDonChangeCents, USDtoValueCents} from "../fields/USDField";
import { ProjectContext } from "../../contexts/ProjectContext";


type BidFormProps = WithLoggedInAuthInfoProps & {
  editable?: boolean;
  create?: boolean;
  bid: BidBaseModel;
  isEditing: boolean;
  onSave: (data: BidBaseModel) => void;
  setIsEditing: (v: boolean) => void;
  onSubmit: (data: BidBaseModel) => void;
  // lets the page react to the amount as it is typed, before it is saved.
  onAmountChange?: (v: number) => void;
  // rendered as a column to the right of the fields, inside this same card.
  aside?: React.ReactNode;
};

function CreateBidForm({
  accessToken,
  create,
  editable,
    bid,
    onSave,
    isEditing,
    setIsEditing,
    onSubmit,
    onAmountChange,
    aside,
  ...props
}: BidFormProps) {

  const { project_id, bid_id } = useParams<string>();
  const userProjectContext = useContext(ProjectContext);
  const navigate = useNavigate();
  const { toast } = useToast();

  const canEdit =
    userProjectContext?.role === CompanyType.builder &&
    bid.status !== BidStatus.Submitted;

  const form = useForm<BidBaseModel>({
    defaultValues: bid
  });
  const bidAmount = form.watch("bid_amount");

  useEffect(() => {
    if (onAmountChange) onAmountChange(bidAmount);
  }, [bidAmount]);

  const onSubmitBid = () => {
      let values = form.getValues();
      onSubmit(values);
  }

  return (
      <Card>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit(onSave)();
            }}
          >
            <CardHeader>
              <CardTitle className="columns-2 flex justify-between">
                <div>Bid</div>
                {form.getValues().status === BidStatus.Submitted &&
                userProjectContext?.role === CompanyType.builder ? (
                  <div
                    className={`p-2 rounded-lg my-2 ${
                      form.getValues().response_status === "rejected"
                        ? "bg-red-100 text-red-800 border border-red-300"
                        : form.getValues().response_status === "in_review"
                        ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                        : (form.getValues().response_status as string) ===
                          "validated"
                        ? "bg-green-100 text-green-800 border border-green-300"
                        : ""
                    }`}
                  >
                    <p>{form.getValues().response_status}</p>
                  </div>
                ) : (
                  <></>
                )}
              </CardTitle>
              {canEdit ? (
                <div className="columns-2 flex justify-between">
                  <div className="items-center flex gap-2">
                    {isEditing ? (
                      <>
                        <Button
                          variant="outline"
                          type="button"
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button variant="outline" type="submit">
                          Save Bid
                        </Button>
                      </>
                    ) : (
                      <Button type="button" onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    )}
                    {/*<Button variant="outline" type="button" onClick={onCopyBid}>*/}
                    {/*  Copy Bid*/}
                    {/*</Button>*/}
                  </div>
                  <SubmitBidDialog
                      onSubmit={onSubmitBid}
                      bid={bid}
                        enabled={bidAmount > 0.0}
                  />
                </div>
              ) : (
                userProjectContext?.role === CompanyType.builder ? (
                  <div>
                    {/*<Button variant="outline" type="button" onClick={onCopyBid}>*/}
                    {/*  Copy Bid*/}
                    {/*</Button>*/}
                  </div>
                ) : <></>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                <div className="flex-1 min-w-0">
              <div className="columns-2">
                <div className="">
                  <FormField
                    control={form.control}
                    name="bid_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Amount</FormLabel>
                        <FormControl>
                          <Input
                            placeholder=""
                            {...field}
                            value={USDtoValueCents(field.value)}
                            className="text-lg disabled:cursor-allowed disabled:opacity-100 disabled:border-none"
                            disabled={!isEditing}
                            onChange={(e) => {
                              field.onChange(USDonChangeCents(e.target.value));
                            }}
                          />
                        </FormControl>
                        <FormDescription></FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder=""
                            {...field}
                            className="disabled:cursor-allowed disabled:opacity-100 disabled:border-none"
                            disabled={!isEditing}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormDescription></FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
                </div>

                {aside &&
                  <div className="w-1/3 shrink-0 border-l pl-6">{aside}</div>
                }
              </div>

              {/* below the top row, so the narrative gets the full card width */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Narrative</FormLabel>
                    <FormControl>
                      <AutosizeTextarea
                        placeholder=""
                        {...field}
                        className="disabled:cursor-allowed disabled:opacity-100 disabled:border-none"
                        disabled={!isEditing}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </form>
        </Form>
      </Card>


  );
}

export default withRequiredAuthInfo(CreateBidForm);
